import { useEffect, useState } from 'react';
import { FieldValues, Path } from 'react-hook-form';
import { AuthFormManagementProps } from '@/type/form.types';
import { AuthField } from '@/components/Form/Auth/AuthField';
import { AuthFieldContainer } from '@/components/Form/Auth/AuthFieldContainer';
import { Input } from '@/components/Controls/Input';
import { twMerge } from 'tailwind-merge';
import { Checkbox } from '@/components/Controls/Checkbox';
import { Link } from 'react-router-dom';
import { AuthButton } from '@/components/Custom/Button/AuthButton';
import { AuthLinkButton } from '@/components/Custom/Button/AuthLinkButton';
import { ErrorMessage } from '@/components/Message/ErrorMessage';

export const LoginManagement = <TSchema extends FieldValues>({
  t,
  responseError,
  setResponseError,
  watch,
  register,
  errors,
  submitDisabled,
  onSubmit,
}: AuthFormManagementProps<TSchema>) => {
  const [showPassword, setShowPassword] = useState(false);

  const emailValue = watch && watch('email' as Path<TSchema>);
  const passwordValue = watch && watch('password' as Path<TSchema>);

  useEffect(() => {
    if (emailValue && passwordValue) {
      setResponseError?.('');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [emailValue, passwordValue]);

  return (
    <form onSubmit={onSubmit}>
      <div className="flex flex-col gap-5">
        <AuthFieldContainer>
          <AuthField title={t('common:email')} required>
            <Input
              {...register('email' as Path<TSchema>)}
              errorMessage={errors.email?.message as string}
              placeholder={t('common:email')}
              type="email"
              inputClassName="h-10"
            />
          </AuthField>
        </AuthFieldContainer>
        <AuthFieldContainer>
          <AuthField title={t('common:password')} required>
            <Input
              {...register('password' as Path<TSchema>)}
              // errorMessage={errors && (errors.password?.message as string)}
              placeholder={t('common:password')}
              type={showPassword ? 'text' : 'password'}
              rightIcon={
                <i
                  className={twMerge(
                    'ki-filled text-gray-500 text-xl hover:cursor-pointer',
                    showPassword ? 'ki-eye-slash' : 'ki-eye'
                  )}
                  onClick={() => setShowPassword((prevState) => !prevState)}
                />
              }
              inputClassName="h-10"
            />
          </AuthField>
        </AuthFieldContainer>
      </div>
      {responseError && (
        <div className="mt-5 flex justify-center">
          <ErrorMessage message={responseError} />
        </div>
      )}
      <div className="mt-5 flex flex-wrap items-center gap-x-5 gap-y-5">
        <div className="flex flex-1">
          <div>
            <Checkbox
              textClassName="text-gray-600 text-md"
              text={t('rememberMe')}
            />
          </div>
        </div>
        <div className="flex justify-end max-xs:w-full max-xs:justify-center">
          <Link
            to="/forgot-password"
            className="text-primary text-md underline hover:cursor-pointer"
          >
            {`${t('forgotPassword')} ?`}
          </Link>
        </div>
      </div>
      <div className="mt-7">
        <AuthButton text={t('common:logIn')} disabled={submitDisabled} />
      </div>
      <div className="mt-5 text-gray-600 text-md text-center">
        {`${t('notRegisteredYet')} ?`}
      </div>
      <div className="mt-5">
        <AuthLinkButton linkTo="/register" text={t('register')} />
      </div>
    </form>
  );
};
